import { PageHeader , Badge , Button , Calendar , Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle , Input , Label , Popover, PopoverContent, PopoverTrigger , Skeleton , Switch } from "@roberttamas/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ColorSchemeSection } from "@/components/color-scheme-section";
import { ConfirmDestructiveDialog } from "@/components/confirm-destructive-dialog";
import { CopyButton } from "@/components/copy-button";
import { CustomizeWithAiButton } from "@/components/customize-with-ai-section";
import { FontSchemeSection } from "@/components/font-scheme-section";
import { CalendarIcon, ExternalLink, Trash2 } from "@/components/icons";
import { LanguageCombobox } from "@/components/language-combobox";
import { LocaleFlag } from "@/components/locale-flag";
import { MenuSection } from "@/components/menu-section";
import { RelativeTime } from "@/components/relative-time";
import { EditDialog, SettingRow } from "@/components/setting-row";
import { SiteHealthStrip } from "@/components/site-health-strip";
import { TemplatePicker } from "@/components/template-picker";
import { TextOverridesRow } from "@/components/text-overrides-row";
import { TimelineSection } from "@/components/timeline-section";
import { ZipStatusCard } from "@/components/zip-status-card";
import { deleteSite, getSite, getDjAccessStatus, listVariants, setDjAccessCode, updateSite, updateSiteConfig } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { tsToDate } from "@/lib/format";
import { currentDateFnsLocale } from "@/lib/locale-format";
import { formatLocaleLabel } from "@/lib/locales";
import type { GetSiteResp } from "@/lib/types";
import { buildUploaderUrl, uploaderDisplayPath } from "@/lib/uploader-url";
import { cn } from "@/lib/utils";

function normalizeDisplayName(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "null") return "";
  return trimmed;
}

/** Invalidate the queries that depend on a single site's data. */
function useSiteInvalidate(siteId: string) {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["site", siteId] }),
      queryClient.invalidateQueries({ queryKey: ["sites"] }),
    ]);
  }, [queryClient, siteId]);
}

export function SiteDetailPage() {
  const { t } = useTranslation();
  const { siteId = "" } = useParams<{ siteId: string }>();

  const query = useQuery<GetSiteResp>({
    queryKey: ["site", siteId],
    queryFn: () => getSite({ siteId }),
    enabled: siteId.length > 0,
  });

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-72 lg:col-span-2" />
          <Skeleton className="h-72" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">{t("siteDetail.loadFailed")}</p>;
  }

  const { site, config, zipStatus, originalFileCount, originalBytes } = query.data;
  const uploaderUrl = buildUploaderUrl(site.slug);
  const language = typeof config.language === "string" ? config.language : "en-US";
  const hasOverrideId = typeof config.textOverrideId === "string" && config.textOverrideId.length > 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        eyebrow={t("sites.title")}
        backTo="/sites"
        backLabel={t("sites.backToSites")}
        title={
          <span className="flex items-center gap-2">
            <span>{site.name}</span>
            {site.archived && <Badge variant="secondary">{t("sites.statusArchived")}</Badge>}
          </span>
        }
        meta={
          <div className="flex items-center gap-1">
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {uploaderDisplayPath(site.slug)}
            </code>
            <CopyButton value={buildUploaderUrl(site.slug)} label={t("siteDetail.copyUploaderUrl")} />
          </div>
        }
        actions={
          <Button asChild variant="outline">
            <a href={uploaderUrl} target="_blank" rel="noreferrer">
              <ExternalLink /> {t("siteDetail.openUploader")}
            </a>
          </Button>
        }
      />

      <SiteHealthStrip
        archived={site.archived}
        zipStatus={zipStatus.status}
        fileCount={originalFileCount}
        bytes={originalBytes}
        expiresAt={site.expiresAt}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">{t("siteDetail.themeTitle")}</CardTitle>
          <CardDescription>{t("siteDetail.themeDesc")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <TemplatePicker siteId={siteId} templateId={config.templateId} />
            <div className="divide-y border-t">
              <ColorSchemeSection siteId={siteId} colorScheme={config.colorScheme} />
              <FontSchemeSection siteId={siteId} fontScheme={config.fontScheme} />
            </div>
          </div>
        </CardContent>
        <CardFooter className="justify-end">
          <CustomizeWithAiButton siteId={siteId} />
        </CardFooter>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Settings — calm read-only rows; each edits in a modal. */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base font-semibold">{t("siteDetail.settingsTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y">
              <IdentitySection
                siteId={siteId}
                name={site.name}
                displayName={normalizeDisplayName(site.displayName)}
              />
              <LocalizationSection siteId={siteId} language={language} />
              <GuestPermissionsSection
                siteId={siteId}
                allowGallery={config.permissions?.allowGallery !== false}
                allowSingleImageDownload={config.permissions?.allowSingleImageDownload !== false}
                allowZipDownload={config.permissions?.allowZipDownload === true}
                allowTimeline={config.permissions?.allowTimeline === true}
                allowMenu={config.permissions?.allowMenu === true}
                allowDjRequests={config.permissions?.allowDjRequests === true}
              />
              <TimelineSection
                siteId={siteId}
                timeline={config.timeline}
                allowTimeline={config.permissions?.allowTimeline === true}
              />
              <MenuSection
                siteId={siteId}
                menu={config.menu}
                allowMenu={config.permissions?.allowMenu === true}
              />
              <DjAccessSection siteId={siteId} />
              <ScheduleSection siteId={siteId} expiresAt={site.expiresAt} />
              <TextOverridesRow
                siteId={siteId}
                language={language}
                textOverrideId={hasOverrideId ? config.textOverrideId : undefined}
              />
            </div>
          </CardContent>
        </Card>

        {/* Operations — automatic; operators mostly confirm success. */}
        <div className="space-y-6">
          <ZipStatusCard siteId={siteId} zipStatus={zipStatus} />
          <ArchiveCard siteId={siteId} archived={site.archived} />
        </div>
      </div>

      <DangerZoneSection
        siteId={siteId}
        siteName={site.name}
        siteSlug={site.slug}
      />
    </div>
  );
}

function EditButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  const { t } = useTranslation();
  return (
    <Button type="button" variant="ghost" size="sm" onClick={onClick} disabled={disabled}>
      {t("common.edit")}
    </Button>
  );
}

function IdentitySection({
  siteId,
  name,
  displayName,
}: {
  siteId: string;
  name: string;
  displayName: string;
}) {
  const { t } = useTranslation();
  const invalidate = useSiteInvalidate(siteId);
  const [open, setOpen] = useState(false);
  const [draftName, setDraftName] = useState(name);
  const [draftDisplay, setDraftDisplay] = useState(displayName);

  const mutation = useMutation({
    mutationFn: async () => {
      const patch: { name?: string; displayName?: string } = {};
      const normalizedDisplay = normalizeDisplayName(draftDisplay);
      if (draftName.trim() !== name) patch.name = draftName.trim();
      if (normalizedDisplay !== displayName) patch.displayName = normalizedDisplay;
      if (Object.keys(patch).length > 0) await updateSite({ siteId, patch });
    },
    onSuccess: async () => {
      toast.success(t("siteDetail.identity.updated"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const openDialog = () => {
    setDraftName(name);
    setDraftDisplay(displayName);
    setOpen(true);
  };
  const dirty = draftName.trim() !== name || draftDisplay.trim() !== displayName;

  return (
    <>
      <SettingRow
        title={t("siteDetail.identity.title")}
        description={t("siteDetail.identity.desc")}
        action={<EditButton onClick={openDialog} />}
        value={
          <div className="space-y-0.5">
            <p className="font-medium">{name}</p>
            <p className="text-muted-foreground">{displayName || t("siteDetail.identity.noDisplayName")}</p>
          </div>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("siteDetail.identity.editTitle")}
        description={t("siteDetail.identity.desc")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={draftName.trim().length === 0 || !dirty}
      >
        <div className="space-y-2">
          <Label htmlFor="edit-name">{t("siteDetail.identity.nameLabel")}</Label>
          <Input
            id="edit-name"
            autoComplete="off"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-display">{t("siteDetail.identity.displayLabel")}</Label>
          <Input
            id="edit-display"
            autoComplete="off"
            placeholder={t("siteDetail.identity.displayPlaceholder")}
            value={draftDisplay}
            onChange={(e) => setDraftDisplay(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t("siteDetail.identity.displayHint")}
          </p>
        </div>
      </EditDialog>
    </>
  );
}

function LocalizationSection({
  siteId,
  language,
}: {
  siteId: string;
  language: string;
}) {
  const { t } = useTranslation();
  const invalidate = useSiteInvalidate(siteId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(language);
  const variantsQuery = useQuery({
    queryKey: ["variants"],
    queryFn: listVariants,
    staleTime: 5 * 60_000,
  });
  const variants = variantsQuery.data?.variants ?? [];

  const mutation = useMutation({
    mutationFn: () => updateSiteConfig({ siteId, configPatch: { language: draft } }),
    onSuccess: async () => {
      toast.success(t("siteDetail.localization.updated"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <>
      <SettingRow
        title={t("siteDetail.localization.title")}
        description={t("siteDetail.localization.desc")}
        action={
          <EditButton
            onClick={() => {
              setDraft(language);
              setOpen(true);
            }}
          />
        }
        value={
          <span className="flex min-w-0 items-center gap-2">
            <LocaleFlag tag={language} />
            <span className="truncate">{formatLocaleLabel(language)}</span>
          </span>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("siteDetail.localization.editTitle")}
        description={t("siteDetail.localization.desc")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={!draft || draft === language}
      >
        <div className="space-y-2">
          <Label htmlFor="edit-language">{t("siteDetail.localization.languageLabel")}</Label>
          <LanguageCombobox
            id="edit-language"
            value={draft}
            options={variants.length > 0 ? variants : [language]}
            onChange={setDraft}
          />
        </div>
      </EditDialog>
    </>
  );
}

function GuestPermissionsSection({
  siteId,
  allowGallery,
  allowSingleImageDownload,
  allowZipDownload,
  allowTimeline,
  allowMenu,
  allowDjRequests,
}: {
  siteId: string;
  allowGallery: boolean;
  allowSingleImageDownload: boolean;
  allowZipDownload: boolean;
  allowTimeline: boolean;
  allowMenu: boolean;
  allowDjRequests: boolean;
}) {
  const { t } = useTranslation();
  const invalidate = useSiteInvalidate(siteId);
  const [open, setOpen] = useState(false);
  const [draftGallery, setDraftGallery] = useState(allowGallery);
  const [draftSingle, setDraftSingle] = useState(allowSingleImageDownload);
  const [draftZip, setDraftZip] = useState(allowZipDownload);
  const [draftTimeline, setDraftTimeline] = useState(allowTimeline);
  const [draftMenu, setDraftMenu] = useState(allowMenu);
  const [draftDj, setDraftDj] = useState(allowDjRequests);

  const mutation = useMutation({
    mutationFn: () =>
      updateSiteConfig({
        siteId,
        configPatch: {
          permissions: {
            allowGallery: draftGallery,
            allowSingleImageDownload: draftSingle,
            allowZipDownload: draftZip,
            allowTimeline: draftTimeline,
            allowMenu: draftMenu,
            allowDjRequests: draftDj,
          },
        },
      }),
    onSuccess: async () => {
      toast.success(t("siteDetail.permissions.updated"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const openDialog = () => {
    setDraftGallery(allowGallery);
    setDraftSingle(allowSingleImageDownload);
    setDraftZip(allowZipDownload);
    setDraftTimeline(allowTimeline);
    setDraftMenu(allowMenu);
    setDraftDj(allowDjRequests);
    setOpen(true);
  };
  // Gallery is the parent of the single photo download: turning it off
  // forces that download off too. The zip archive download is independent.
  const setGallery = (on: boolean) => {
    setDraftGallery(on);
    if (!on) {
      setDraftSingle(false);
    }
  };
  const dirty =
    draftGallery !== allowGallery ||
    draftSingle !== allowSingleImageDownload ||
    draftZip !== allowZipDownload ||
    draftTimeline !== allowTimeline ||
    draftMenu !== allowMenu ||
    draftDj !== allowDjRequests;

  return (
    <>
      <SettingRow
        title={t("siteDetail.permissions.title")}
        description={t("siteDetail.permissions.desc")}
        action={<EditButton onClick={openDialog} />}
        value={
          <div className="space-y-1">
            <p>
              {t("siteDetail.permissions.gallery")}:{" "}
              <span className="font-medium">
                {allowGallery ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
            </p>
            <div
              className={cn(
                "ml-1 space-y-0.5 border-l pl-3",
                !allowGallery && "text-muted-foreground",
              )}
            >
              <p>
                {t("siteDetail.permissions.singlePhoto")}:{" "}
                <span className="font-medium">
                  {allowGallery && allowSingleImageDownload
                    ? t("siteDetail.permissions.on")
                    : t("siteDetail.permissions.off")}
                </span>
              </p>
            </div>
            <p>
              {t("siteDetail.permissions.zip")}:{" "}
              <span className="font-medium">
                {allowZipDownload ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
            </p>
            <p>
              {t("siteDetail.permissions.timeline")}:{" "}
              <span className="font-medium">
                {allowTimeline ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
            </p>
            <p>
              {t("siteDetail.permissions.menu")}:{" "}
              <span className="font-medium">
                {allowMenu ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
            </p>
            <p>
              {t("siteDetail.permissions.dj")}:{" "}
              <span className="font-medium">
                {allowDjRequests ? t("siteDetail.permissions.on") : t("siteDetail.permissions.off")}
              </span>
            </p>
          </div>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("siteDetail.permissions.editTitle")}
        description={t("siteDetail.permissions.editDesc")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={!dirty}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="allow-gallery">{t("siteDetail.permissions.gallery")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("siteDetail.permissions.galleryHint")}
            </p>
          </div>
          <Switch
            id="allow-gallery"
            checked={draftGallery}
            onCheckedChange={setGallery}
          />
        </div>
        <div className="ml-1 space-y-4 border-l pl-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-1">
              <Label htmlFor="allow-single">{t("siteDetail.permissions.singlePhotoDownload")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("siteDetail.permissions.singleHint")}
              </p>
            </div>
            <Switch
              id="allow-single"
              checked={draftSingle}
              disabled={!draftGallery}
              onCheckedChange={setDraftSingle}
            />
          </div>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="allow-zip">{t("siteDetail.permissions.zipDownload")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("siteDetail.permissions.zipHint")}
            </p>
          </div>
          <Switch
            id="allow-zip"
            checked={draftZip}
            onCheckedChange={setDraftZip}
          />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="allow-timeline">{t("siteDetail.permissions.timeline")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("siteDetail.permissions.timelineHint")}
            </p>
          </div>
          <Switch
            id="allow-timeline"
            checked={draftTimeline}
            onCheckedChange={setDraftTimeline}
          />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="allow-menu">{t("siteDetail.permissions.menu")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("siteDetail.permissions.menuHint")}
            </p>
          </div>
          <Switch
            id="allow-menu"
            checked={draftMenu}
            onCheckedChange={setDraftMenu}
          />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 space-y-1">
            <Label htmlFor="allow-dj">{t("siteDetail.permissions.dj")}</Label>
            <p className="text-xs text-muted-foreground">
              {t("siteDetail.permissions.djHint")}
            </p>
          </div>
          <Switch
            id="allow-dj"
            checked={draftDj}
            onCheckedChange={setDraftDj}
          />
        </div>
      </EditDialog>
    </>
  );
}

function DjAccessSection({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftCode, setDraftCode] = useState("");
  const statusQuery = useQuery({
    queryKey: ["djAccess", siteId],
    queryFn: () => getDjAccessStatus({ siteId }),
  });
  const isSet = statusQuery.data?.isSet ?? false;

  const mutation = useMutation({
    mutationFn: (code: string | null) => setDjAccessCode({ siteId, code }),
    onSuccess: async (resp) => {
      toast.success(resp.isSet ? t("siteDetail.dj.saved") : t("siteDetail.dj.cleared"));
      await statusQuery.refetch();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const trimmed = draftCode.trim();
  const valid = trimmed === "" || /^[A-Za-z0-9]{4,12}$/.test(trimmed);

  return (
    <>
      <SettingRow
        title={t("siteDetail.dj.title")}
        description={t("siteDetail.dj.desc")}
        action={
          <EditButton
            onClick={() => {
              setDraftCode("");
              setOpen(true);
            }}
          />
        }
        value={
          <span className="font-medium">{isSet ? t("siteDetail.dj.set") : t("siteDetail.dj.notSet")}</span>
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("siteDetail.dj.title")}
        description={t("siteDetail.dj.dialogDesc")}
        onSave={() => mutation.mutate(trimmed === "" ? null : trimmed)}
        saving={mutation.isPending}
        saveDisabled={!valid || (trimmed === "" && !isSet)}
      >
        <div className="space-y-2">
          <Label htmlFor="dj-code">{t("siteDetail.dj.pinLabel")}</Label>
          <Input
            id="dj-code"
            autoComplete="off"
            placeholder={isSet ? t("siteDetail.dj.replacePlaceholder") : t("siteDetail.dj.examplePlaceholder")}
            value={draftCode}
            onChange={(e) => setDraftCode(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            {t("siteDetail.dj.hint")}
          </p>
        </div>
      </EditDialog>
    </>
  );
}

function ScheduleSection({
  siteId,
  expiresAt,
}: {
  siteId: string;
  expiresAt: GetSiteResp["site"]["expiresAt"];
}) {
  const { t } = useTranslation();
  const invalidate = useSiteInvalidate(siteId);
  const currentDate = tsToDate(expiresAt);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Date | null>(currentDate);

  const currentIso = currentDate ? currentDate.toISOString() : null;
  const draftIso = draft ? draft.toISOString() : null;

  const mutation = useMutation({
    mutationFn: () => updateSite({ siteId, patch: { expiresAt: draftIso } }),
    onSuccess: async () => {
      toast.success(t("siteDetail.schedule.updated"));
      await invalidate();
      setOpen(false);
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <>
      <SettingRow
        title={t("siteDetail.schedule.title")}
        description={t("siteDetail.schedule.desc")}
        action={
          <EditButton
            onClick={() => {
              setDraft(currentDate);
              setOpen(true);
            }}
          />
        }
        value={
          currentDate ? (
            <span>
              <RelativeTime date={currentDate} />
              {", "}
              {format(currentDate, "PPP", { locale: currentDateFnsLocale() })}
            </span>
          ) : (
            <span className="text-muted-foreground">{t("expiry.none")}</span>
          )
        }
      />
      <EditDialog
        open={open}
        onOpenChange={setOpen}
        title={t("siteDetail.schedule.editTitle")}
        description={t("siteDetail.schedule.desc")}
        onSave={() => mutation.mutate()}
        saving={mutation.isPending}
        saveDisabled={draftIso === currentIso}
      >
        <div className="space-y-2">
          <Label>{t("siteDetail.schedule.expiresAt")}</Label>
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn("w-full justify-start", !draft && "text-muted-foreground")}
                >
                  <CalendarIcon />
                  {draft ? format(draft, "PPP", { locale: currentDateFnsLocale() }) : t("expiry.none")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={draft ?? undefined}
                  onSelect={(d) => setDraft(d ?? null)}
                  disabled={(d) => d.getTime() <= Date.now()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {draft && (
              <Button type="button" variant="ghost" onClick={() => setDraft(null)}>
                {t("common.clear")}
              </Button>
            )}
          </div>
        </div>
      </EditDialog>
    </>
  );
}

/**
 * Standalone Archive card. Lives in the right column under the zip archive —
 * archiving is a routine availability switch, not a destructive action, so it
 * stays out of the danger zone.
 */
function ArchiveCard({ siteId, archived }: { siteId: string; archived: boolean }) {
  const { t } = useTranslation();
  const invalidate = useSiteInvalidate(siteId);

  const archiveMutation = useMutation({
    mutationFn: (next: boolean) => updateSite({ siteId, patch: { archived: next } }),
    onSuccess: async (_resp, next) => {
      toast.success(next ? t("siteDetail.archive.archived") : t("siteDetail.archive.unarchived"));
      await invalidate();
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-semibold">{t("siteDetail.archive.title")}</CardTitle>
        <Switch
          aria-label={t("siteDetail.archive.archivedAria")}
          checked={archived}
          disabled={archiveMutation.isPending}
          onCheckedChange={(v) => archiveMutation.mutate(v)}
        />
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {t("siteDetail.archive.desc")}
        </p>
      </CardContent>
    </Card>
  );
}

interface DangerZoneProps {
  siteId: string;
  siteName: string;
  siteSlug: string;
}

function DangerZoneSection({ siteId, siteName, siteSlug }: DangerZoneProps) {
  const { t } = useTranslation();
  const invalidate = useSiteInvalidate(siteId);
  const navigate = useNavigate();

  const deleteMutation = useMutation({
    mutationFn: (confirmSlug: string) => deleteSite({ siteId, confirmSlug }),
    onSuccess: async (_resp, confirmSlug) => {
      toast.success(t("siteDetail.danger.deleted", { slug: confirmSlug }));
      await invalidate();
      navigate("/sites");
    },
    // On failure we just toast — the dialog stays open so the operator can
    // retry; nothing is deleted unless the whole delete succeeds.
    onError: (err) => toast.error(toAppError(err).message),
  });

  return (
    <Card className="border-destructive/30">
      <CardHeader>
        <CardTitle className="text-base font-semibold text-destructive">{t("siteDetail.danger.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="space-y-1">
            <h3 className="text-sm font-medium text-destructive">{t("siteDetail.danger.deleteSite")}</h3>
            <p className="text-xs text-muted-foreground">
              {t("siteDetail.danger.deleteDesc")}
            </p>
          </div>
          <ConfirmDestructiveDialog
            trigger={
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                disabled={deleteMutation.isPending}
              >
                <Trash2 /> {t("siteDetail.danger.deleteSite")}
              </Button>
            }
            title={t("siteDetail.danger.dialogTitle", { name: siteName })}
            description={
              <Trans
                i18nKey="siteDetail.danger.dialogDesc"
                values={{ path: uploaderDisplayPath(siteSlug) }}
                components={{ c: <code className="font-mono" /> }}
              />
            }
            confirmWord={siteSlug}
            confirmLabel={t("siteDetail.danger.deleteForever")}
            busyLabel={t("siteDetail.danger.deleting")}
            onConfirm={async () => {
              await deleteMutation.mutateAsync(siteSlug);
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
